import type { ConfigState } from '@renderer/types/configState'
import type { HubDocument } from '@renderer/types/hubConfig'
import { fetchProjectCatalogRaw, fetchProjectVersion, type RawProjectCatalog } from './projectCatalogApi'

/**
 * Persisted project config + catalogue cache (multi-project hub plan §4.4).
 *
 * Today an offline booth keeps working because `ProjectRoute` mounts once at
 * boot. A hub re-runs that bootstrap on every session (hub → member → hub), so
 * a failed `/api/projects/version` fetch would otherwise strand every guest on
 * `NotFound`. This cache is the fallback for that path only: online, the
 * network still wins (`cache: 'no-cache'` semantics are untouched); offline,
 * the last config and catalogue the booth saw for a project are replayed
 * through the exact same store setters.
 *
 * Bounded to a handful of projects (a hub plus its members); the oldest entry
 * is evicted first. Every read/write is wrapped — localStorage can be full or
 * absent (tests, private windows) and a cache must never break a session.
 */

export const PROJECT_CONFIG_CACHE_KEY = 'project-config-cache-v1'
export const PROJECT_CONFIG_CACHE_MAX_ENTRIES = 8

export interface CachedProject {
  effectiveUpdatedAt?: string
  config?: ConfigState
  catalog?: RawProjectCatalog
  /**
   * Raw `GET /api/hubs?id=` document, for entries keyed by a HUB id. Stored
   * unprojected so an offline replay runs through the same `hubToConfig` as a
   * live load — a stored projection would freeze whatever the shape was on the
   * day it was cached.
   */
  hub?: HubDocument
  savedAt: number
}

type CacheFile = Record<string, CachedProject>

interface StorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

let storageOverride: StorageLike | null = null

/**
 * Strictly increasing stamp for cache ordering.
 *
 * `savedAt` is what decides which entry is evicted at the bound and which one
 * survives a full localStorage, and `Date.now()` only has millisecond
 * resolution — two cache writes inside one tick is ordinary, not exotic
 * (`cacheProjectConfig` immediately followed by `cacheProjectCatalog`, or a
 * hub warming its members). Tied stamps make `sort` fall back to insertion
 * order, which is exactly backwards: the OLDEST entry sorts first and wins.
 *
 * Counting up from the last stamp instead keeps ordering total within a
 * process. The value can run a few ms ahead of the wall clock under churn;
 * nothing reads it as a date.
 */
let lastSavedAt = 0
function nextSavedAt(): number {
  const now = Date.now()
  lastSavedAt = now > lastSavedAt ? now : lastSavedAt + 1
  return lastSavedAt
}

/** Tests swap in an in-memory storage; the renderer uses localStorage. */
export function setProjectConfigCacheStorage(storage: StorageLike | null): void {
  storageOverride = storage
}

function storage(): StorageLike | null {
  if (storageOverride) return storageOverride
  try {
    if (typeof window !== 'undefined' && window.localStorage) return window.localStorage
  } catch {
    // Access to localStorage itself can throw (sandboxed frames).
  }
  return null
}

function readFile(): CacheFile {
  const s = storage()
  if (!s) return {}
  try {
    const raw = s.getItem(PROJECT_CONFIG_CACHE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as CacheFile)
      : {}
  } catch {
    return {}
  }
}

/**
 * `priorityId` is the entry this write exists to save. On a full storage it is
 * the one kept, rather than whichever happens to sort first — otherwise the
 * fallback can throw away the config just fetched and keep a stale project's,
 * leaving an offline booth to replay the wrong config. Omitted by callers that
 * are only reshaping the file (prune), where newest-wins is the right rule.
 */
function writeFile(file: CacheFile, priorityId?: string): void {
  const s = storage()
  if (!s) return
  // Evict oldest entries beyond the bound BEFORE serialising, so a full
  // localStorage recovers on the next write instead of failing forever.
  const entries = Object.entries(file).sort((a, b) => b[1].savedAt - a[1].savedAt)
  const bounded = Object.fromEntries(entries.slice(0, PROJECT_CONFIG_CACHE_MAX_ENTRIES))
  try {
    s.setItem(PROJECT_CONFIG_CACHE_KEY, JSON.stringify(bounded))
  } catch (error) {
    // Quota exceeded: drop everything but one entry and try once more.
    console.warn('[PROJECT-CACHE] write failed, retrying with a single entry:', error)
    try {
      const survivor =
        priorityId && file[priorityId] ? ([priorityId, file[priorityId]] as const) : entries[0]
      s.setItem(
        PROJECT_CONFIG_CACHE_KEY,
        JSON.stringify(survivor ? { [survivor[0]]: survivor[1] } : {})
      )
    } catch {
      // Give up silently — the cache is a fallback, never a requirement.
    }
  }
}

function upsert(projectId: string, patch: Partial<CachedProject>): void {
  if (!projectId) return
  const file = readFile()
  file[projectId] = { ...(file[projectId] ?? { savedAt: 0 }), ...patch, savedAt: nextSavedAt() }
  writeFile(file, projectId)
}

export function readCachedProject(projectId: string): CachedProject | undefined {
  if (!projectId) return undefined
  return readFile()[projectId]
}

export function cacheProjectConfig(
  projectId: string,
  effectiveUpdatedAt: string | undefined,
  config: ConfigState
): void {
  upsert(projectId, { effectiveUpdatedAt, config })
}

export function cacheProjectCatalog(projectId: string, catalog: RawProjectCatalog): void {
  upsert(projectId, { catalog })
}

/** Hub twin of {@link cacheProjectConfig}; keyed by hub id in the same file. */
export function cacheHubDocument(
  hubId: string,
  effectiveUpdatedAt: string | undefined,
  hub: HubDocument
): void {
  upsert(hubId, { effectiveUpdatedAt, hub })
}

/** Keep only `keepIds` (a hub and its members); called when a hub loads. */
export function pruneProjectCache(keepIds: string[]): void {
  const keep = new Set(keepIds)
  const file = readFile()
  let changed = false
  for (const id of Object.keys(file)) {
    if (!keep.has(id)) {
      delete file[id]
      changed = true
    }
  }
  if (changed) writeFile(file)
}

export function clearProjectCache(): void {
  const s = storage()
  if (!s) return
  try {
    s.removeItem(PROJECT_CONFIG_CACHE_KEY)
  } catch {
    // ignore
  }
}

const inflightPrefetch = new Map<string, Promise<void>>()

/**
 * Warm one project's config + catalogue into the cache without touching any
 * store. Skips the download when the cached `effectiveUpdatedAt` already
 * matches the server's. Never throws — a prefetch failure only means the
 * member is fetched cold on pick, exactly as today.
 */
export async function prefetchProjectIntoCache(projectId: string): Promise<void> {
  if (!projectId) return
  const existing = inflightPrefetch.get(projectId)
  if (existing) return existing

  const run = (async (): Promise<void> => {
    try {
      const effectiveUpdatedAt = await fetchProjectVersion(projectId)
      if (!effectiveUpdatedAt) return
      const cached = readCachedProject(projectId)
      if (cached?.effectiveUpdatedAt === effectiveUpdatedAt && cached.config && cached.catalog) {
        return
      }
      const projectResponse = await fetch(
        `${process.env.VITE_API_URL}/api/projects?id=${projectId}`,
        { credentials: 'include', cache: 'no-cache' }
      )
      if (!projectResponse.ok) return
      const config = (await projectResponse.json()) as ConfigState
      if (!config || config._id !== projectId) return
      const catalog = await fetchProjectCatalogRaw(projectId)
      upsert(projectId, { effectiveUpdatedAt, config, catalog })
    } catch (error) {
      console.warn(`[PROJECT-CACHE] prefetch failed for ${projectId}:`, error)
    } finally {
      inflightPrefetch.delete(projectId)
    }
  })()

  inflightPrefetch.set(projectId, run)
  return run
}

/**
 * Called when a hub's config lands: keep the cache scoped to this hub and its
 * members, then warm each member one at a time (a booth on a weak link should
 * not fan out a dozen requests the moment Welcome appears).
 */
export async function prefetchHubMembers(hubId: string, memberIds: string[]): Promise<void> {
  pruneProjectCache([hubId, ...memberIds])
  for (const id of memberIds) {
    await prefetchProjectIntoCache(id)
  }
}
